import { Surface, Text } from '@shh/ui';
import type { PlanState } from '@shh/shared';
import { usePlan } from '../../components/Plus';
import { plusText } from '../../i18n/plus';

/** Which words fit: a family whose trial has run out reads "Probemonat vorbei", not just "free". */
const phase = (p: PlanState) =>
  p.kind === 'free' && p.trialEnds !== undefined ? ('trialOver' as const) : p.kind;

/** Under the closed section title: where the family stands, at a glance. */
export function PlusSectionHint() {
  const plan = usePlan();
  const k = phase(plan);
  return k === 'trial'
    ? plusText.sectionHint.trial(plan.trialDaysLeft ?? 1)
    : plusText.sectionHint[k];
}

/**
 * Settings → Larovi Plus (1.21): the family's status and what Plus includes – four parts, as
 * decided with Mike (14.10.). No buying yet: payment comes in its own release.
 */
export function PlusSettings() {
  const plan = usePlan();
  const k = phase(plan);
  const status =
    k === 'trial' ? plusText.status.trial(plan.trialDaysLeft ?? 1) : plusText.status[k];
  return (
    <div className="flex flex-col gap-section">
      <Surface className="flex flex-col gap-gap-xs bg-accent-secondary-soft">
        <span className="text-sm font-bold text-accent-secondary">{plusText.statusTitle}</span>
        <span className="font-display text-xl font-black">{status}</span>
        <Text>{plusText.statusText[k]}</Text>
      </Surface>
      <section aria-labelledby="plus-parts" className="flex flex-col gap-gap-sm">
        <h3 id="plus-parts" className="font-display text-lg font-black">
          {plusText.partsTitle}
        </h3>
        <ul className="grid gap-gap-sm sm:grid-cols-2">
          {plusText.parts.map((p) => (
            <li key={p.title}>
              <Surface className="flex h-full flex-col gap-gap-xs">
                <span className="font-semibold">{p.title}</span>
                <span className="text-sm text-text-secondary">{p.text}</span>
              </Surface>
            </li>
          ))}
        </ul>
      </section>
      <section aria-labelledby="plus-soon" className="flex flex-col gap-gap-xs">
        <h3 id="plus-soon" className="font-display text-lg font-black">
          {plusText.soonTitle}
        </h3>
        <Text size="sm" tone="secondary">
          {plusText.soon}
        </Text>
      </section>
      <Text size="sm" tone="secondary">
        {plusText.freeStays}{' '}
        {plan.kind !== 'plus' && plan.kind !== 'testfamily' ? plusText.buySoon : ''}
      </Text>
    </div>
  );
}
